import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toolbar } from "@/components/Toolbar";
import { Pitch } from "@/components/Pitch";
import { InfoPanel } from "@/components/InfoPanel";
import { SessionsDialog } from "@/components/SessionsDialog";
import { ActivityTabs } from "@/components/ActivityTabs";
import { SessionBuilder } from "@/components/SessionBuilder";
import { SessionPlanPreview } from "@/components/SessionPlanPreview";
import { UserMenu } from "@/components/UserMenu";
import { AuthDialog } from "@/components/AuthDialog";
import { DraftRestoreDialog } from "@/components/DraftRestoreDialog";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { OBJECT_TYPES, PITCH_MODES, FIELD_COLORS, DEFAULT_FIELD_COLOR, getFieldPreset, isPlayerLikeType } from "@/constants/pitchObjects";
import { toast, Toaster } from "sonner";
import { useHistoryState } from "@/lib/useHistoryState";
import { exportSvgAsPng } from "@/lib/exportPng";
import { createFreshWorkspace } from "@/lib/workspaceState";
import { normalizeLoadedActivity, withFreshIds } from "@/lib/activityLoad";
import { isSingleActivityWorkspace, shouldConfirmReplaceActiveTab } from "@/lib/unsavedWork";
import { revisionOf } from "@/lib/recordRevision";
import { useUnsavedWorkProtection } from "@/hooks/useUnsavedWorkProtection";
import { Undo2, Redo2, Download, Save, FolderOpen, FilePlus, Copy, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

let _idc = 0;
const nextId = (prefix) => `${prefix}-${Date.now().toString(36)}-${(_idc++).toString(36)}`;

// Compute a default placement point inside the current viewport, in
// canonical coordinates. Placed near the viewport centre with a small
// grid spread so consecutive placements do not overlap.
const defaultDropPoint = (mode, index) => {
  const meta = PITCH_MODES[mode];
  const vp = meta.viewport;
  const bx = vp.x + vp.w / 2 + ((index % 5) - 2) * 32;
  const by = vp.y + vp.h / 2 + Math.floor(index / 5) * 32;
  return { x: bx, y: by };
};

// Per-type default size for newly-placed objects. Player-like types
// use the Small/Medium/Large preset system (defaulting to "medium");
// everything else uses a numeric size multiplier as before.
const DEFAULT_SIZE = {
  goal: 1.5,
  miniGoal: 1.5,
  player: "medium",
  defender: "medium",
  neutral: "medium",
  goalkeeper: "medium",
  coach: "medium",
  // ball, cone, mannequin -> 1.0 (fallback below)
};

const EMPTY_BOARD = { objects: [], arrows: [], shapes: [], texts: [], lines: [] };
const DEFAULT_ACTIVITY = {
  name: "",
  // Rules replaces the old free-text description in the panel.
  rules: "",
  // Structured duration: (time + rest) × sets = total. `time`/`sets`/`rest`
  // are the source of truth; the calculated total is derived in the panel.
  time: "",
  sets: "",
  rest: "",
  // Ordered list of coaching points — each { id, type, text }.
  coachingPoints: [],
  // Ordered list of player rotations — each { id, from, to }. Free-
  // text swap pairs (e.g. { from: "CB", to: "CM" }) surfaced in the
  // Info Panel and Session Plan; never rendered on the pitch canvas.
  rotations: [],
};

// Coerce a duration input (possibly empty / string / negative / NaN) to a
// non-negative number for the total calculation.
const toDurNum = (v) => {
  const n = Number(v);
  return isFinite(n) && n > 0 ? n : 0;
};
export const computeTotalMinutes = (a) =>
  (toDurNum(a.time) + toDurNum(a.rest)) * toDurNum(a.sets);

const IconButton = ({ label, onClick, disabled, testId, children, tone = "neutral", className = "" }) => {
  const base =
    "h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors duration-150 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2";
  const toneCls =
    tone === "primary"
      ? "bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
      : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40 disabled:hover:bg-white";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className={`${base} ${toneCls} ${className}`}
            aria-label={label}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="font-medium">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default function TrainingGround() {
  const [pitchMode, setPitchMode] = useState("vertical-full");
  const [activeTool, setActiveTool] = useState(null);
  const [keepToolActive, setKeepToolActive] = useState(false);
  const [placementColor, setPlacementColor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [selectedArrowId, setSelectedArrowId] = useState(null);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [selectedLineId, setSelectedLineId] = useState(null);
  // multiSelection is an array of { kind: 'object'|'shape'|'text'|'arrow'|'line', id }
  const [multiSelection, setMultiSelection] = useState([]);
  const [drawStyle, setDrawStyle] = useState({ color: "#0F172A", dashed: false });
  const [showDimensions, setShowDimensions] = useState(true);
  const [fieldColor, setFieldColor] = useState(DEFAULT_FIELD_COLOR);
  const [activity, setActivity] = useState(DEFAULT_ACTIVITY);
  const [activityId, setActivityId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  // NP-4 — revision of the persisted record each id points at (null = no
  // persisted record). Sent back as `baseRevision` on a normal save. The
  // activity revision travels with activityId in every tab entry.
  const [activityRevision, setActivityRevision] = useState(null);
  const [sessionRevision, setSessionRevision] = useState(null);
  const [sessionName, setSessionName] = useState("");
  // Session-level planning metadata (independent of any single activity).
  const [sessionMeta, setSessionMeta] = useState({
    date: "",
    startTime: "",
    location: "",
    objective: "",
    load: "",
    offensivePrinciples: [],
    defensivePrinciples: [],
  });
  const [sessionsMode, setSessionsMode] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  // Auth-gated action: if user tries to Save/Load without being signed in,
  // we open the AuthDialog and re-attempt the action after sign-in.
  const { user: authUser } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingSessionsMode, setPendingSessionsMode] = useState(null);
  const requireAuth = useCallback((mode) => {
    if (authUser && typeof authUser === "object") {
      setSessionsMode(mode);
    } else {
      setPendingSessionsMode(mode);
      setAuthOpen(true);
    }
  }, [authUser]);
  // Once auth completes, resume the pending save/load action.
  useEffect(() => {
    if (authUser && typeof authUser === "object" && pendingSessionsMode) {
      setSessionsMode(pendingSessionsMode);
      setPendingSessionsMode(null);
    }
  }, [authUser, pendingSessionsMode]);
  // Which top-level view is on screen. "editor" is the existing canvas
  // workspace, "builder" is the session overview.
  const [view, setView] = useState("editor");

  const history = useHistoryState(EMPTY_BOARD);
  const { state: board, set, setSilent, checkpoint, undo, redo, canUndo, canRedo, reset } = history;
  const { objects, arrows, shapes, texts, lines } = board;

  // -------------------------------------------------------------
  // Multi-activity workspace. The existing hooks above hold the LIVE
  // state of the currently-active tab. Non-active tabs are parked in
  // `workspaceTabs` as snapshots and hydrated back into the live hooks
  // whenever the coach switches tabs.
  // -------------------------------------------------------------
  const makeTabId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const [workspaceTabs, setWorkspaceTabs] = useState(() => [
    {
      tabId: makeTabId(),
      activity: DEFAULT_ACTIVITY,
      activityId: null,
      pitchMode: "vertical-full",
      fieldColor: DEFAULT_FIELD_COLOR,
      showDimensions: true,
      board: EMPTY_BOARD,
    },
  ]);
  const [activeTabId, setActiveTabId] = useState(() => workspaceTabs[0].tabId);

  const pitchRef = useRef(null);

  const selectedObject = useMemo(
    () => objects.find((o) => o.id === selectedId) || null,
    [objects, selectedId]
  );
  const selectedShape = useMemo(
    () => shapes.find((s) => s.id === selectedShapeId) || null,
    [shapes, selectedShapeId]
  );
  const selectedArrow = useMemo(
    () => arrows.find((a) => a.id === selectedArrowId) || null,
    [arrows, selectedArrowId]
  );
  const selectedText = useMemo(
    () => texts.find((t) => t.id === selectedTextId) || null,
    [texts, selectedTextId]
  );
  const selectedLine = useMemo(
    () => lines.find((l) => l.id === selectedLineId) || null,
    [lines, selectedLineId]
  );

  // Selecting any one kind clears the others so only one thing is active.
  const selectObject = (id) => {
    setSelectedId(id);
    if (id) {
      setSelectedShapeId(null);
      setSelectedArrowId(null);
      setSelectedTextId(null);
      setSelectedLineId(null);
      setMultiSelection([]);
    }
  };
  const selectShape = (id) => {
    setSelectedShapeId(id);
    if (id) {
      setSelectedId(null);
      setSelectedArrowId(null);
      setSelectedTextId(null);
      setSelectedLineId(null);
      setMultiSelection([]);
    }
  };
  const selectArrow = (id) => {
    setSelectedArrowId(id);
    if (id) {
      setSelectedId(null);
      setSelectedShapeId(null);
      setSelectedTextId(null);
      setSelectedLineId(null);
      setMultiSelection([]);
    }
  };
  const selectText = (id) => {
    setSelectedTextId(id);
    if (id) {
      setSelectedId(null);
      setSelectedShapeId(null);
      setSelectedArrowId(null);
      setSelectedLineId(null);
      setMultiSelection([]);
    }
  };
  const selectLine = (id) => {
    setSelectedLineId(id);
    if (id) {
      setSelectedId(null);
      setSelectedShapeId(null);
      setSelectedArrowId(null);
      setSelectedTextId(null);
      setMultiSelection([]);
    }
  };
  const clearSelections = () => {
    setSelectedId(null);
    setSelectedShapeId(null);
    setSelectedArrowId(null);
    setSelectedTextId(null);
    setSelectedLineId(null);
    setMultiSelection([]);
  };

  // Shift+click toggle: add or remove one item to/from the running
  // multi-selection. If a single item is currently selected we promote it
  // into the group first so the user can build the group incrementally.
  const handleToggleSelection = (kind, id) => {
    const currentSet = new Set(
      (multiSelection || []).map((m) => `${m.kind}:${m.id}`)
    );
    const singleEntry =
      (selectedId && { kind: "object", id: selectedId }) ||
      (selectedShapeId && { kind: "shape", id: selectedShapeId }) ||
      (selectedArrowId && { kind: "arrow", id: selectedArrowId }) ||
      (selectedTextId && { kind: "text", id: selectedTextId }) ||
      (selectedLineId && { kind: "line", id: selectedLineId }) ||
      null;
    if (singleEntry) currentSet.add(`${singleEntry.kind}:${singleEntry.id}`);
    const key = `${kind}:${id}`;
    if (currentSet.has(key)) currentSet.delete(key);
    else currentSet.add(key);
    setSelectedId(null);
    setSelectedShapeId(null);
    setSelectedArrowId(null);
    setSelectedTextId(null);
    setSelectedLineId(null);
    setMultiSelection(
      Array.from(currentSet).map((k) => {
        const idx = k.indexOf(":");
        return { kind: k.slice(0, idx), id: k.slice(idx + 1) };
      })
    );
  };

  // Shared nudge: move the currently selected item(s) by (dx, dy) canonical
  // units. Used by Arrow-key stepping.
  const nudgeSelection = (dx, dy) => {
    if (dx === 0 && dy === 0) return;
    checkpoint();
    if (multiSelection && multiSelection.length > 0) {
      set((cur) => {
        const oIds = new Set(multiSelection.filter((m) => m.kind === "object").map((m) => m.id));
        const sIds = new Set(multiSelection.filter((m) => m.kind === "shape").map((m) => m.id));
        const tIds = new Set(multiSelection.filter((m) => m.kind === "text").map((m) => m.id));
        const aIds = new Set(multiSelection.filter((m) => m.kind === "arrow").map((m) => m.id));
        const lIds = new Set(multiSelection.filter((m) => m.kind === "line").map((m) => m.id));
        return {
          ...cur,
          objects: cur.objects.map((o) => (oIds.has(o.id) ? { ...o, x: o.x + dx, y: o.y + dy } : o)),
          shapes: cur.shapes.map((s) => {
            if (!sIds.has(s.id)) return s;
            if (s.type === "rect") return { ...s, x: s.x + dx, y: s.y + dy };
            if (s.type === "circle") return { ...s, cx: s.cx + dx, cy: s.cy + dy };
            return s;
          }),
          texts: cur.texts.map((t) => (tIds.has(t.id) ? { ...t, x: t.x + dx, y: t.y + dy } : t)),
          arrows: cur.arrows.map((a) => {
            if (!aIds.has(a.id)) return a;
            const next = { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy };
            if (typeof a.cx === "number" && typeof a.cy === "number") {
              next.cx = a.cx + dx;
              next.cy = a.cy + dy;
            }
            return next;
          }),
          lines: cur.lines.map((l) =>
            lIds.has(l.id)
              ? { ...l, x1: l.x1 + dx, y1: l.y1 + dy, x2: l.x2 + dx, y2: l.y2 + dy }
              : l
          ),
        };
      });
      return;
    }
    if (selectedId) {
      set((cur) => ({
        ...cur,
        objects: cur.objects.map((o) => (o.id === selectedId ? { ...o, x: o.x + dx, y: o.y + dy } : o)),
      }));
    } else if (selectedShapeId) {
      set((cur) => ({
        ...cur,
        shapes: cur.shapes.map((s) => {
          if (s.id !== selectedShapeId) return s;
          if (s.type === "rect") return { ...s, x: s.x + dx, y: s.y + dy };
          if (s.type === "circle") return { ...s, cx: s.cx + dx, cy: s.cy + dy };
          return s;
        }),
      }));
    } else if (selectedTextId) {
      set((cur) => ({
        ...cur,
        texts: cur.texts.map((t) => (t.id === selectedTextId ? { ...t, x: t.x + dx, y: t.y + dy } : t)),
      }));
    } else if (selectedArrowId) {
      set((cur) => ({
        ...cur,
        arrows: cur.arrows.map((a) => {
          if (a.id !== selectedArrowId) return a;
          const next = { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy };
          if (typeof a.cx === "number" && typeof a.cy === "number") {
            next.cx = a.cx + dx;
            next.cy = a.cy + dy;
          }
          return next;
        }),
      }));
    } else if (selectedLineId) {
      set((cur) => ({
        ...cur,
        lines: cur.lines.map((l) =>
          l.id === selectedLineId
            ? { ...l, x1: l.x1 + dx, y1: l.y1 + dy, x2: l.x2 + dx, y2: l.y2 + dy }
            : l
        ),
      }));
    }
  };

  const stats = useMemo(
    () => ({
      objects: objects.length,
      arrows: arrows.length + lines.length,
      shapes: shapes.length + texts.length,
      players: objects.filter((o) => isPlayerLikeType(o.type)).length,
    }),
    [objects, arrows, shapes, texts, lines]
  );

  const addObjectOfType = useCallback(
    (type, at) => {
      const def = OBJECT_TYPES[type];
      const sameCount = objects.filter((o) => o.type === type).length;
      const drop = at || defaultDropPoint(pitchMode, objects.length);
      const isNumbered = ["player", "defender", "neutral"].includes(type);
      // Goalkeeper uses "GK", Coach uses "C" — every other player-like
      // is numbered by placement order. Equipment stays label-free.
      const label = isNumbered
        ? String(sameCount + 1)
        : (type === "goalkeeper" || type === "coach")
          ? def.shortLabel
          : "";
      const size = DEFAULT_SIZE[type] ?? 1;
      const isPlayerLike = isPlayerLikeType(type);
      const newObj = { id: nextId(type), type, x: drop.x, y: drop.y, label, size, rotation: 0 };
      // Newly created Player / Defender / Neutral / Goalkeeper default to
      // BLANK label mode — coaches typically hide numbers until they're
      // deliberately assigned. Coach is intentionally excluded so it keeps
      // its visible "C" out of the box. Existing saved players are never
      // touched (this only fires on creation).
      if (isPlayerLike && type !== "coach") {
        newObj.displayMode = "blank";
      }
      // Apply the pre-selected placement colour (if any) to new player-like
      // objects so successive placements share the chosen colour.
      if (isPlayerLike && placementColor) newObj.color = placementColor;
      set((cur) => ({ ...cur, objects: [...cur.objects, newObj] }));
      selectObject(newObj.id);
    },
    [objects, pitchMode, set, placementColor]
  );

  const handleSelectTool = (key) => {
    // Clicking any tool button (including ADD object types) only activates
    // the placement tool — no object is created until the user clicks the
    // pitch. Clicking the same button again toggles it off.
    setActiveTool((cur) => (cur === key ? null : key));
  };

  const handleAddText = (cx, cy) => {
    const newText = {
      id: nextId("text"),
      x: cx,
      y: cy,
      content: "Text",
      color: drawStyle.color || "#0F172A",
      size: 1,
      rotation: 0,
    };
    set((cur) => ({ ...cur, texts: [...cur.texts, newText] }));
    selectText(newText.id);
    setActiveTool(null);
  };

  const handlePitchClick = (cx, cy) => {
    // Reject clicks outside the current viewport (letterboxed padding
    // inside the SVG element must never spawn objects). The click's
    // (cx, cy) are already in canonical coordinates thanks to Pitch's
    // view→canonical conversion, so we compare against the viewport rect.
    const vp = PITCH_MODES[pitchMode]?.viewport;
    const insidePitch =
      cx >= vp.x && cx <= vp.x + vp.w && cy >= vp.y && cy <= vp.y + vp.h;
    if (!insidePitch) {
      // Treat as a deselect click.
      clearSelections();
      return;
    }
    if (activeTool === "text") {
      handleAddText(cx, cy);
      return;
    }
    if (activeTool && OBJECT_TYPES[activeTool]) {
      addObjectOfType(activeTool, { x: cx, y: cy });
      // After placing an ADD object: stay in the placement tool if the
      // user opted in via Keep Tool Active, otherwise return to Select.
      if (!keepToolActive) setActiveTool("select");
      return;
    }
    clearSelections();
  };

  const handleDragStart = useCallback(() => {
    checkpoint();
  }, [checkpoint]);

  const handleMoveObject = (id, x, y) => {
    setSilent((cur) => ({
      ...cur,
      objects: cur.objects.map((o) => (o.id === id ? { ...o, x, y } : o)),
    }));
  };

  const handleMoveShape = (id, x, y) => {
    setSilent((cur) => ({
      ...cur,
      shapes: cur.shapes.map((s) => {
        if (s.id !== id) return s;
        if (s.type === "rect") return { ...s, x, y };
        if (s.type === "circle") return { ...s, cx: x, cy: y };
        return s;
      }),
    }));
  };

  const handleResizeShape = (id, patch) => {
    setSilent((cur) => ({
      ...cur,
      shapes: cur.shapes.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  // Persist the user-adjusted offset for a shape's dimension annotation.
  // The offset is applied relative to the shape's default annotation
  // anchor, so the annotation stays associated even after the shape
  // itself moves. Uses setSilent for smooth dragging; the initial
  // onDragStart (from Pitch) checkpoint makes a single Undo reverse it.
  const handleMoveShapeDim = (id, dimOffset) => {
    setSilent((cur) => ({
      ...cur,
      shapes: cur.shapes.map((s) => (s.id === id ? { ...s, dimOffset } : s)),
    }));
  };

  const handleMoveText = (id, x, y) => {
    setSilent((cur) => ({
      ...cur,
      texts: cur.texts.map((t) => (t.id === id ? { ...t, x, y } : t)),
    }));
  };

  const handleMoveArrow = (id, patch) => {
    setSilent((cur) => ({
      ...cur,
      arrows: cur.arrows.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    }));
  };

  const handleMoveArrowEndpoint = (id, which, x, y) => {
    setSilent((cur) => ({
      ...cur,
      arrows: cur.arrows.map((a) =>
        a.id === id
          ? { ...a, ...(which === "start" ? { x1: x, y1: y } : { x2: x, y2: y }) }
          : a
      ),
    }));
  };

  // Adjust an arrow's quadratic Bezier control point so the body curves to
  // pass near the given canonical point. Endpoints remain anchored.
  const handleCurveArrow = (id, cx, cy) => {
    setSilent((cur) => ({
      ...cur,
      arrows: cur.arrows.map((a) => (a.id === id ? { ...a, cx, cy } : a)),
    }));
  };

  const handleMoveLine = (id, patch) => {
    setSilent((cur) => ({
      ...cur,
      lines: cur.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    }));
  };

  const handleMoveLineEndpoint = (id, which, x, y) => {
    setSilent((cur) => ({
      ...cur,
      lines: cur.lines.map((l) =>
        l.id === id
          ? { ...l, ...(which === "start" ? { x1: x, y1: y } : { x2: x, y2: y }) }
          : l
      ),
    }));
  };

  const handleDuplicateArrow = (id) => {
    const src = arrows.find((a) => a.id === id);
    if (!src) return null;
    const newA = { ...src, id: nextId("arrow") };
    setSilent((cur) => ({ ...cur, arrows: [...cur.arrows, newA] }));
    selectArrow(newA.id);
    return newA.id;
  };

  const handleDuplicateLine = (id) => {
    const src = lines.find((l) => l.id === id);
    if (!src) return null;
    const newL = { ...src, id: nextId("line") };
    setSilent((cur) => ({ ...cur, lines: [...cur.lines, newL] }));
    selectLine(newL.id);
    return newL.id;
  };

  // Apply a delta (dx, dy) to a batch of items — used when dragging one item
  // in a multi-selection so the rest follow along.
  const handleMultiDelta = (dx, dy, others) => {
    if (!others || others.length === 0) return;
    setSilent((cur) => {
      const oIds = new Set(others.filter((m) => m.kind === "object").map((m) => m.id));
      const sIds = new Set(others.filter((m) => m.kind === "shape").map((m) => m.id));
      const tIds = new Set(others.filter((m) => m.kind === "text").map((m) => m.id));
      const aIds = new Set(others.filter((m) => m.kind === "arrow").map((m) => m.id));
      const lIds = new Set(others.filter((m) => m.kind === "line").map((m) => m.id));
      return {
        ...cur,
        objects: cur.objects.map((o) =>
          oIds.has(o.id) ? { ...o, x: o.x + dx, y: o.y + dy } : o
        ),
        shapes: cur.shapes.map((s) => {
          if (!sIds.has(s.id)) return s;
          if (s.type === "rect") return { ...s, x: s.x + dx, y: s.y + dy };
          if (s.type === "circle") return { ...s, cx: s.cx + dx, cy: s.cy + dy };
          return s;
        }),
        texts: cur.texts.map((t) =>
          tIds.has(t.id) ? { ...t, x: t.x + dx, y: t.y + dy } : t
        ),
        arrows: cur.arrows.map((a) => {
          if (!aIds.has(a.id)) return a;
          const next = { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy };
          if (typeof a.cx === "number" && typeof a.cy === "number") {
            next.cx = a.cx + dx;
            next.cy = a.cy + dy;
          }
          return next;
        }),
        lines: cur.lines.map((l) =>
          lIds.has(l.id)
            ? { ...l, x1: l.x1 + dx, y1: l.y1 + dy, x2: l.x2 + dx, y2: l.y2 + dy }
            : l
        ),
      };
    });
  };

  // Group resize — scale every targeted object's position (relative to
  // the group centre) AND its size by the same factor, so the coach can
  // grow or shrink a whole selection with one handle while relative
  // spacing is preserved. Sizes are stored as numeric scales in [0.35, 3]
  // exactly like the per-player resize handle.
  const handleMultiScale = (factor, center, targets) => {
    if (!targets || targets.length === 0) return;
    const clampedFactor = Math.max(0.05, Math.min(20, factor));
    const byId = new Map(
      targets.map((t) => [
        t.id,
        {
          x: center.x + (t.x0 - center.x) * clampedFactor,
          y: center.y + (t.y0 - center.y) * clampedFactor,
          size: Math.max(0.35, Math.min(3, t.scale0 * clampedFactor)),
        },
      ])
    );
    setSilent((cur) => ({
      ...cur,
      objects: cur.objects.map((o) =>
        byId.has(o.id) ? { ...o, ...byId.get(o.id) } : o
      ),
    }));
  };

  // Given a canonical bounding box, compute which items fall inside and
  // stage a multi-selection.
  const handleMarqueeSelect = (box) => {
    const { x1, y1, x2, y2 } = box;
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    const within = (x, y) => x >= minX && x <= maxX && y >= minY && y <= maxY;
    const picked = [];
    objects.forEach((o) => within(o.x, o.y) && picked.push({ kind: "object", id: o.id }));
    shapes.forEach((s) => {
      const cx = s.type === "rect" ? s.x + (s.w || 0) / 2 : s.cx;
      const cy = s.type === "rect" ? s.y + (s.h || 0) / 2 : s.cy;
      if (within(cx, cy)) picked.push({ kind: "shape", id: s.id });
    });
    texts.forEach((t) => within(t.x, t.y) && picked.push({ kind: "text", id: t.id }));
    arrows.forEach((a) => {
      const cx = (a.x1 + a.x2) / 2;
      const cy = (a.y1 + a.y2) / 2;
      if (within(cx, cy)) picked.push({ kind: "arrow", id: a.id });
    });
    lines.forEach((l) => {
      const cx = (l.x1 + l.x2) / 2;
      const cy = (l.y1 + l.y2) / 2;
      if (within(cx, cy)) picked.push({ kind: "line", id: l.id });
    });
    setSelectedId(null);
    setSelectedShapeId(null);
    setSelectedArrowId(null);
    setSelectedTextId(null);
    setSelectedLineId(null);
    setMultiSelection(picked);
    if (picked.length > 0) toast.success(`${picked.length} item${picked.length === 1 ? "" : "s"} selected`);
  };

  // ------------------------------------------------------------------
  // Global z-order across ALL canvas item collections.
  // ------------------------------------------------------------------
  // We keep the existing collections (`objects`, `shapes`, `arrows`,
  // `texts`, `lines`) intact — a per-item `z` field controls the visual
  // stacking order across every kind. Any reorder rewrites `z` on every
  // affected item so the state is self-consistent after each action.
  const Z_KIND_BASE = { shape: 0, arrow: 1, line: 2, text: 3, object: 4 };
  const effectiveItemZ = (kind, item, insertionIdx) =>
    typeof item.z === "number"
      ? item.z
      : (Z_KIND_BASE[kind] || 0) * 1_000_000 + insertionIdx;

  // Produce a flat sorted list of {kind, id} tuples across every
  // collection, matching the Pitch render order.
  const buildGlobalOrder = (state) => {
    const entries = [];
    state.shapes.forEach((s, i) => entries.push({ kind: "shape", id: s.id, z: effectiveItemZ("shape", s, i) }));
    state.arrows.forEach((a, i) => entries.push({ kind: "arrow", id: a.id, z: effectiveItemZ("arrow", a, i) }));
    state.lines.forEach((l, i) => entries.push({ kind: "line", id: l.id, z: effectiveItemZ("line", l, i) }));
    state.texts.forEach((t, i) => entries.push({ kind: "text", id: t.id, z: effectiveItemZ("text", t, i) }));
    state.objects.forEach((o, i) => entries.push({ kind: "object", id: o.id, z: effectiveItemZ("object", o, i) }));
    return entries.sort((a, b) => a.z - b.z);
  };

  // Apply new z values from a re-ordered flat list back into the
  // per-collection state, stepping z by 10 so future single-item
  // inserts still land above/below adjacent items cleanly.
  const applyOrderToState = (cur, orderedIds) => {
    const zByKindId = new Map();
    orderedIds.forEach(({ kind, id }, i) => zByKindId.set(`${kind}:${id}`, (i + 1) * 10));
    const mapItems = (arr, kind) =>
      arr.map((it) => {
        const zv = zByKindId.get(`${kind}:${it.id}`);
        return typeof zv === "number" ? { ...it, z: zv } : it;
      });
    return {
      ...cur,
      objects: mapItems(cur.objects, "object"),
      shapes: mapItems(cur.shapes, "shape"),
      arrows: mapItems(cur.arrows, "arrow"),
      lines: mapItems(cur.lines, "line"),
      texts: mapItems(cur.texts, "text"),
    };
  };

  // Collect the current selection as an array of {kind, id}.
  const getSelectionKindIds = () => {
    if (multiSelection && multiSelection.length > 0) {
      return multiSelection.map((m) => ({ kind: m.kind, id: m.id }));
    }
    if (selectedId) return [{ kind: "object", id: selectedId }];
    if (selectedShapeId) return [{ kind: "shape", id: selectedShapeId }];
    if (selectedArrowId) return [{ kind: "arrow", id: selectedArrowId }];
    if (selectedTextId) return [{ kind: "text", id: selectedTextId }];
    if (selectedLineId) return [{ kind: "line", id: selectedLineId }];
    return [];
  };

  const applyGlobalZ = (mode) => {
    const sel = getSelectionKindIds();
    if (sel.length === 0) return;
    set((cur) => {
      const order = buildGlobalOrder(cur);
      const selKey = new Set(sel.map((s) => `${s.kind}:${s.id}`));
      const isSel = (e) => selKey.has(`${e.kind}:${e.id}`);
      // Split into selected (preserving current relative order) + others.
      const selectedOrdered = order.filter(isSel);
      let newOrder;
      if (mode === "front") {
        // Selected to top: others first (unchanged relative order), then selected.
        const others = order.filter((e) => !isSel(e));
        newOrder = [...others, ...selectedOrdered];
      } else if (mode === "back") {
        const others = order.filter((e) => !isSel(e));
        newOrder = [...selectedOrdered, ...others];
      } else if (mode === "forward") {
        // Move the selected block up by one step: find the first
        // non-selected item that appears immediately above ANY selected
        // item and drop it just below the group's bottom.
        // Concretely: locate the highest-indexed selected item, then take
        // the item at (topSel + 1) — call it X — and place it just below
        // the LOWEST-indexed selected item.
        const topSelIdx = order.reduce(
          (acc, e, i) => (isSel(e) ? i : acc),
          -1
        );
        if (topSelIdx === -1 || topSelIdx === order.length - 1) {
          newOrder = order;
        } else {
          const X = order[topSelIdx + 1];
          const bottomSelIdx = order.findIndex(isSel);
          // Build new order by removing X and re-inserting just before bottomSelIdx.
          const withoutX = [...order.slice(0, topSelIdx + 1), ...order.slice(topSelIdx + 2)];
          newOrder = [
            ...withoutX.slice(0, bottomSelIdx),
            X,
            ...withoutX.slice(bottomSelIdx),
          ];
        }
      } else {
        // backward: mirror of forward.
        const bottomSelIdx = order.findIndex(isSel);
        const topSelIdx = order.reduce((acc, e, i) => (isSel(e) ? i : acc), -1);
        if (bottomSelIdx <= 0) {
          newOrder = order;
        } else {
          const X = order[bottomSelIdx - 1];
          const withoutX = [...order.slice(0, bottomSelIdx - 1), ...order.slice(bottomSelIdx)];
          // After removing X, topSelIdx shifts down by 1.
          const insertAt = topSelIdx; // (topSelIdx - 1) + 1
          newOrder = [
            ...withoutX.slice(0, insertAt + 1),
            X,
            ...withoutX.slice(insertAt + 1),
          ];
        }
      }
      return applyOrderToState(cur, newOrder);
    });
    const label = {
      forward: "Brought forward",
      backward: "Sent backward",
      front: "Brought to front",
      back: "Sent to back",
    }[mode];
    toast.success(label);
  };

  // Shift+click on the existing buttons jumps straight to front/back —
  // reuses the current UI (no new controls) but exposes the full behavior
  // required by the spec.
  const handleBringForward = (e) => applyGlobalZ(e && e.shiftKey ? "front" : "forward");
  const handleSendBackward = (e) => applyGlobalZ(e && e.shiftKey ? "back" : "backward");

  const handleUpdateSelectedText = (patch) => {
    if (!selectedTextId) return;
    set((cur) => ({
      ...cur,
      texts: cur.texts.map((t) => (t.id === selectedTextId ? { ...t, ...patch } : t)),
    }));
  };

  // Alt-drag clone helpers — the drag starts on the ORIGINAL element and we
  // spawn a new one at the same coordinates; the caller then re-targets the
  // drag onto the clone. onDragStart runs first so a single undo removes
  // both the clone and the drag movement.
  const handleDuplicateObject = (id) => {
    const src = objects.find((o) => o.id === id);
    if (!src) return null;
    const newObj = { ...src, id: nextId(src.type) };
    setSilent((cur) => ({ ...cur, objects: [...cur.objects, newObj] }));
    selectObject(newObj.id);
    return newObj.id;
  };
  const handleDuplicateShape = (id) => {
    const src = shapes.find((s) => s.id === id);
    if (!src) return null;
    const newSh = { ...src, id: nextId("shape") };
    setSilent((cur) => ({ ...cur, shapes: [...cur.shapes, newSh] }));
    return newSh.id;
  };
  const handleDuplicateText = (id) => {
    const src = texts.find((t) => t.id === id);
    if (!src) return null;
    const newT = { ...src, id: nextId("text") };
    setSilent((cur) => ({ ...cur, texts: [...cur.texts, newT] }));
    return newT.id;
  };

  const handleUpdateSelectedLabel = (label) => {
    if (!selectedId) return;
    set((cur) => ({
      ...cur,
      objects: cur.objects.map((o) => (o.id === selectedId ? { ...o, label } : o)),
    }));
  };

  // Player-only: switch which stored value is displayed. Never overwrites
  // any of the three stored values (number/initials/position). Works for
  // either a single selection or a multi-selection — in the multi case it
  // applies to every player-like object in the selection as a single
  // undoable history action, ignoring non-player selections.
  const handleUpdatePlayerDisplayMode = (mode) => {
    if (multiSelection && multiSelection.length > 0) {
      const ids = new Set(
        multiSelection.filter((m) => m.kind === "object").map((m) => m.id)
      );
      if (ids.size === 0) return;
      set((cur) => ({
        ...cur,
        objects: cur.objects.map((o) =>
          ids.has(o.id) && isPlayerLikeType(o.type) ? { ...o, displayMode: mode } : o
        ),
      }));
      return;
    }
    if (!selectedId) return;
    set((cur) => ({
      ...cur,
      objects: cur.objects.map((o) =>
        o.id === selectedId ? { ...o, displayMode: mode } : o
      ),
    }));
  };

  // Edit ONE of the three independent player values without touching the
  // other two.
  const handleUpdatePlayerDisplayValue = (mode, value) => {
    if (!selectedId) return;
    const key = mode === "initials" ? "initials" : mode === "position" ? "position" : "label";
    set((cur) => ({
      ...cur,
      objects: cur.objects.map((o) =>
        o.id === selectedId ? { ...o, [key]: value } : o
      ),
    }));
  };

  // Where the player-colour controls should appear (single / multi / placement)
  // and what colour is currently "active" for highlighting.
  const playerColorContext = useMemo(() => {
    const singlePlayer =
      selectedId && objects.find((o) => o.id === selectedId && isPlayerLikeType(o.type));
    if (singlePlayer) {
      return {
        show: true,
        mode: "single",
        activeColor: singlePlayer.color || OBJECT_TYPES[singlePlayer.type].fill,
      };
    }
    const multiIds = new Set(
      (multiSelection || []).filter((m) => m.kind === "object").map((m) => m.id)
    );
    if (multiIds.size > 0) {
      const multiPlayers = objects.filter(
        (o) => multiIds.has(o.id) && isPlayerLikeType(o.type)
      );
      if (multiPlayers.length > 0) {
        const colors = new Set(
          multiPlayers.map((p) => p.color || OBJECT_TYPES[p.type].fill)
        );
        return {
          show: true,
          mode: "multi",
          activeColor: colors.size === 1 ? [...colors][0] : null,
          count: multiPlayers.length,
        };
      }
    }
    if (activeTool && isPlayerLikeType(activeTool)) {
      return {
        show: true,
        mode: "placement",
        activeColor: placementColor || OBJECT_TYPES[activeTool].fill,
      };
    }
    return { show: false };
  }, [selectedId, multiSelection, objects, activeTool, placementColor]);

  // Whether the collective Display-mode toggle should surface in the
  // multi-selection panel, and which mode (if any) is currently agreed
  // upon across every selected player-like object.
  const playerDisplayModeContext = useMemo(() => {
    const multiIds = new Set(
      (multiSelection || []).filter((m) => m.kind === "object").map((m) => m.id)
    );
    if (multiIds.size === 0) return { show: false };
    const players = objects.filter((o) => multiIds.has(o.id) && isPlayerLikeType(o.type));
    if (players.length === 0) return { show: false };
    const modes = new Set(players.map((p) => p.displayMode || "number"));
    return {
      show: true,
      activeMode: modes.size === 1 ? [...modes][0] : null,
      count: players.length,
    };
  }, [multiSelection, objects]);

  // Whether the collective Size preset toggle should surface, and
  // which preset (if any) is currently shared across every selected
  // player-like object. Mirrors playerColorContext so the same
  // {show, mode, count, activeSize} shape drives both the single- and
  // multi-selection editors in InfoPanel.
  const playerSizeContext = useMemo(() => {
    const singlePlayer =
      selectedId && objects.find((o) => o.id === selectedId && isPlayerLikeType(o.type));
    if (singlePlayer) {
      return {
        show: true,
        mode: "single",
        activeSize: (typeof singlePlayer.size === "string" && singlePlayer.size) || "medium",
      };
    }
    const multiIds = new Set(
      (multiSelection || []).filter((m) => m.kind === "object").map((m) => m.id)
    );
    if (multiIds.size === 0) return { show: false };
    const players = objects.filter((o) => multiIds.has(o.id) && isPlayerLikeType(o.type));
    if (players.length === 0) return { show: false };
    const sizes = new Set(
      players.map((p) => (typeof p.size === "string" && p.size) || "medium")
    );
    return {
      show: true,
      mode: "multi",
      activeSize: sizes.size === 1 ? [...sizes][0] : null,
      count: players.length,
    };
  }, [selectedId, multiSelection, objects]);

  // Per-instance colour for player-like objects. Handles three cases:
  //   1) Multi-selection with any player-like items → colour them all
  //   2) A single player-like object selected → colour that one
  //   3) No player selected but a player-like placement tool is active →
  //      remember the choice as the placement colour so the next objects
  //      placed inherit it.
  const handleUpdatePlayerColor = (color) => {
    if (multiSelection && multiSelection.length > 0) {
      const targetIds = new Set(
        multiSelection
          .filter((m) => m.kind === "object")
          .map((m) => m.id)
      );
      if (targetIds.size > 0) {
        set((cur) => ({
          ...cur,
          objects: cur.objects.map((o) =>
            targetIds.has(o.id) && isPlayerLikeType(o.type) ? { ...o, color } : o
          ),
        }));
      }
      return;
    }
    if (selectedId) {
      const obj = objects.find((o) => o.id === selectedId);
      if (obj && isPlayerLikeType(obj.type)) {
        set((cur) => ({
          ...cur,
          objects: cur.objects.map((o) => (o.id === selectedId ? { ...o, color } : o)),
        }));
      }
      return;
    }
    if (activeTool && isPlayerLikeType(activeTool)) {
      setPlacementColor(color);
    }
  };

  // Size preset for player-like objects. Accepts one of PLAYER_SIZE_KEYS
  // ("small" | "medium" | "large") and behaves in two modes:
  //   1) Multi-selection with any player-like items → apply to all
  //      player-like items in the selection. Non-player items in the
  //      selection are left untouched.
  //   2) A single player-like object selected → apply to that one.
  // The free-form numeric resize handle is disabled for player-like
  // objects so this preset string is the sole source of truth for a
  // player's visual size.
  const handleUpdatePlayerSize = (sizeKey) => {
    if (multiSelection && multiSelection.length > 0) {
      const targetIds = new Set(
        multiSelection
          .filter((m) => m.kind === "object")
          .map((m) => m.id)
      );
      if (targetIds.size > 0) {
        set((cur) => ({
          ...cur,
          objects: cur.objects.map((o) =>
            targetIds.has(o.id) && isPlayerLikeType(o.type) ? { ...o, size: sizeKey } : o
          ),
        }));
      }
      return;
    }
    if (selectedId) {
      const obj = objects.find((o) => o.id === selectedId);
      if (obj && isPlayerLikeType(obj.type)) {
        set((cur) => ({
          ...cur,
          objects: cur.objects.map((o) => (o.id === selectedId ? { ...o, size: sizeKey } : o)),
        }));
      }
    }
  };

  const handleUpdateSelectedSize = (size) => {
    if (!selectedId) return;
    setSilent((cur) => ({
      ...cur,
      objects: cur.objects.map((o) => (o.id === selectedId ? { ...o, size } : o)),
    }));
  };

  const handleUpdateSelectedRotation = (rotation) => {
    if (!selectedId) return;
    setSilent((cur) => ({
      ...cur,
      objects: cur.objects.map((o) => (o.id === selectedId ? { ...o, rotation } : o)),
    }));
  };

  const handleDeleteSelected = () => {
    if (multiSelection && multiSelection.length > 0) {
      const oIds = new Set(multiSelection.filter((m) => m.kind === "object").map((m) => m.id));
      const sIds = new Set(multiSelection.filter((m) => m.kind === "shape").map((m) => m.id));
      const tIds = new Set(multiSelection.filter((m) => m.kind === "text").map((m) => m.id));
      const aIds = new Set(multiSelection.filter((m) => m.kind === "arrow").map((m) => m.id));
      const lIds = new Set(multiSelection.filter((m) => m.kind === "line").map((m) => m.id));
      const count = multiSelection.length;
      set((cur) => ({
        ...cur,
        objects: cur.objects.filter((o) => !oIds.has(o.id)),
        shapes: cur.shapes.filter((s) => !sIds.has(s.id)),
        texts: cur.texts.filter((t) => !tIds.has(t.id)),
        arrows: cur.arrows.filter((a) => !aIds.has(a.id)),
        lines: cur.lines.filter((l) => !lIds.has(l.id)),
      }));
      setMultiSelection([]);
      toast.success(`${count} item${count === 1 ? "" : "s"} removed`);
      return;
    }
    if (selectedId) {
      set((cur) => ({ ...cur, objects: cur.objects.filter((o) => o.id !== selectedId) }));
      setSelectedId(null);
      toast.success("Object removed");
      return;
    }
    if (selectedShapeId) {
      set((cur) => ({ ...cur, shapes: cur.shapes.filter((s) => s.id !== selectedShapeId) }));
      setSelectedShapeId(null);
      toast.success("Shape removed");
      return;
    }
    if (selectedTextId) {
      set((cur) => ({ ...cur, texts: cur.texts.filter((t) => t.id !== selectedTextId) }));
      setSelectedTextId(null);
      toast.success("Text removed");
      return;
    }
    if (selectedArrowId) {
      set((cur) => ({ ...cur, arrows: cur.arrows.filter((a) => a.id !== selectedArrowId) }));
      setSelectedArrowId(null);
      toast.success("Arrow removed");
      return;
    }
    if (selectedLineId) {
      set((cur) => ({ ...cur, lines: cur.lines.filter((l) => l.id !== selectedLineId) }));
      setSelectedLineId(null);
      toast.success("Line removed");
    }
  };

  // Cmd/Ctrl+D duplication for the current selection (single or multi).
  // A fixed canonical offset keeps duplicates visible while preserving
  // relative positions across a group. Uses `set` (not setSilent) so a
  // single Undo reverses the whole action.
  const DUP_OFFSET = 24;
  const cloneObject = (o) => ({ ...o, id: nextId(o.type), x: o.x + DUP_OFFSET, y: o.y + DUP_OFFSET });
  const cloneShape = (s) => {
    const base = { ...s, id: nextId("shape") };
    if (s.type === "rect") return { ...base, x: s.x + DUP_OFFSET, y: s.y + DUP_OFFSET };
    if (s.type === "circle") return { ...base, cx: s.cx + DUP_OFFSET, cy: s.cy + DUP_OFFSET };
    return base;
  };
  const cloneText = (t) => ({ ...t, id: nextId("text"), x: t.x + DUP_OFFSET, y: t.y + DUP_OFFSET });
  const cloneArrow = (a) => {
    const next = {
      ...a,
      id: nextId("arrow"),
      x1: a.x1 + DUP_OFFSET,
      y1: a.y1 + DUP_OFFSET,
      x2: a.x2 + DUP_OFFSET,
      y2: a.y2 + DUP_OFFSET,
    };
    if (typeof a.cx === "number" && typeof a.cy === "number") {
      next.cx = a.cx + DUP_OFFSET;
      next.cy = a.cy + DUP_OFFSET;
    }
    return next;
  };
  const cloneLine = (l) => ({
    ...l,
    id: nextId("line"),
    x1: l.x1 + DUP_OFFSET,
    y1: l.y1 + DUP_OFFSET,
    x2: l.x2 + DUP_OFFSET,
    y2: l.y2 + DUP_OFFSET,
  });

  const handleDuplicateSelection = () => {
    if (multiSelection && multiSelection.length > 0) {
      const oIds = new Set(multiSelection.filter((m) => m.kind === "object").map((m) => m.id));
      const sIds = new Set(multiSelection.filter((m) => m.kind === "shape").map((m) => m.id));
      const tIds = new Set(multiSelection.filter((m) => m.kind === "text").map((m) => m.id));
      const aIds = new Set(multiSelection.filter((m) => m.kind === "arrow").map((m) => m.id));
      const lIds = new Set(multiSelection.filter((m) => m.kind === "line").map((m) => m.id));
      const newObjs = objects.filter((o) => oIds.has(o.id)).map(cloneObject);
      const newShapes = shapes.filter((s) => sIds.has(s.id)).map(cloneShape);
      const newTexts = texts.filter((t) => tIds.has(t.id)).map(cloneText);
      const newArrows = arrows.filter((a) => aIds.has(a.id)).map(cloneArrow);
      const newLines = lines.filter((l) => lIds.has(l.id)).map(cloneLine);
      const total =
        newObjs.length + newShapes.length + newTexts.length + newArrows.length + newLines.length;
      if (total === 0) return;
      set((cur) => ({
        ...cur,
        objects: [...cur.objects, ...newObjs],
        shapes: [...cur.shapes, ...newShapes],
        texts: [...cur.texts, ...newTexts],
        arrows: [...cur.arrows, ...newArrows],
        lines: [...cur.lines, ...newLines],
      }));
      // Select the newly duplicated items so subsequent actions target them.
      const nextSel = [
        ...newObjs.map((o) => ({ kind: "object", id: o.id })),
        ...newShapes.map((s) => ({ kind: "shape", id: s.id })),
        ...newTexts.map((t) => ({ kind: "text", id: t.id })),
        ...newArrows.map((a) => ({ kind: "arrow", id: a.id })),
        ...newLines.map((l) => ({ kind: "line", id: l.id })),
      ];
      setSelectedId(null);
      setSelectedShapeId(null);
      setSelectedArrowId(null);
      setSelectedTextId(null);
      setSelectedLineId(null);
      setMultiSelection(nextSel);
      toast.success(`${total} item${total === 1 ? "" : "s"} duplicated`);
      return;
    }
    if (selectedId) {
      const src = objects.find((o) => o.id === selectedId);
      if (!src) return;
      const clone = cloneObject(src);
      set((cur) => ({ ...cur, objects: [...cur.objects, clone] }));
      selectObject(clone.id);
      toast.success("Duplicated");
      return;
    }
    if (selectedShapeId) {
      const src = shapes.find((s) => s.id === selectedShapeId);
      if (!src) return;
      const clone = cloneShape(src);
      set((cur) => ({ ...cur, shapes: [...cur.shapes, clone] }));
      selectShape(clone.id);
      toast.success("Duplicated");
      return;
    }
    if (selectedTextId) {
      const src = texts.find((t) => t.id === selectedTextId);
      if (!src) return;
      const clone = cloneText(src);
      set((cur) => ({ ...cur, texts: [...cur.texts, clone] }));
      selectText(clone.id);
      toast.success("Duplicated");
      return;
    }
    if (selectedArrowId) {
      const src = arrows.find((a) => a.id === selectedArrowId);
      if (!src) return;
      const clone = cloneArrow(src);
      set((cur) => ({ ...cur, arrows: [...cur.arrows, clone] }));
      selectArrow(clone.id);
      toast.success("Duplicated");
      return;
    }
    if (selectedLineId) {
      const src = lines.find((l) => l.id === selectedLineId);
      if (!src) return;
      const clone = cloneLine(src);
      set((cur) => ({ ...cur, lines: [...cur.lines, clone] }));
      selectLine(clone.id);
      toast.success("Duplicated");
    }
  };

  // Change the color / dashed style of the currently selected shape/arrow/line.
  // Also updates the default drawStyle so the next draw follows suit.
  const handleUpdateDrawStyle = (patch) => {
    const next = { ...drawStyle, ...patch };
    setDrawStyle(next);
    if (selectedShapeId) {
      set((cur) => ({
        ...cur,
        shapes: cur.shapes.map((s) => (s.id === selectedShapeId ? { ...s, ...patch } : s)),
      }));
    }
    if (selectedArrowId) {
      set((cur) => ({
        ...cur,
        arrows: cur.arrows.map((a) => (a.id === selectedArrowId ? { ...a, ...patch } : a)),
      }));
    }
    if (selectedLineId) {
      set((cur) => ({
        ...cur,
        lines: cur.lines.map((l) => (l.id === selectedLineId ? { ...l, ...patch } : l)),
      }));
    }
  };

  const handleClearPitch = () => {
    if (
      objects.length === 0 &&
      arrows.length === 0 &&
      shapes.length === 0 &&
      texts.length === 0 &&
      lines.length === 0
    )
      return;
    set(EMPTY_BOARD);
    clearSelections();
    toast.success("Pitch cleared");
  };

  const handleAddArrow = (draft) => {
    set((cur) => ({ ...cur, arrows: [...cur.arrows, { id: nextId("arrow"), ...draft }] }));
  };

  const handleAddShape = (draft) => {
    set((cur) => ({ ...cur, shapes: [...cur.shapes, { id: nextId("shape"), ...draft }] }));
  };

  const handleAddLine = (draft) => {
    set((cur) => ({ ...cur, lines: [...cur.lines, { id: nextId("line"), ...draft }] }));
  };

  const handleExportPng = async () => {
    const svg = pitchRef.current?.getSvg();
    if (!svg) return;
    try {
      const safeName = (activity.name || "training-ground")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60);
      await exportSvgAsPng(svg, `${safeName || "training-ground"}.png`);
      toast.success("PNG downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  // "New" starts a completely new, empty session (DI-2). It must reset the
  // WHOLE workspace — every tab plus the session identity (id, name,
  // details) — otherwise a later "Save Session" would still target the
  // previously loaded session id and overwrite it. Because this discards
  // every tab, ask first whenever there is meaningful UNSAVED work (DI-1).
  const handleNewSession = () => {
    if (
      !protection.confirmDiscard(
        "Start a new session? Every activity tab will be cleared and any unsaved changes will be lost. Saved sessions are not affected."
      )
    ) {
      return;
    }
    const fresh = createFreshWorkspace(makeTabId);
    setWorkspaceTabs(fresh.tabs);
    setActiveTabId(fresh.activeTabId);
    hydrateFromEntry(fresh.tabs[0]);
    setSessionId(fresh.sessionId);
    setSessionRevision(null);
    setSessionName(fresh.sessionName);
    setSessionMeta(fresh.sessionMeta);
    clearSelections();
    setActiveTool(null);
    protection.markClean();
    toast.success("New session started");
  };

  // -------------------------------------------------------------
  // Tab switching / creation. On switch we snapshot the live state of the
  // currently-active tab into `workspaceTabs` and hydrate the live hooks
  // from the target tab so the single canvas simply re-renders.
  // -------------------------------------------------------------
  const captureLiveSnapshot = () => ({
    activity,
    activityId,
    activityRevision,
    pitchMode,
    fieldColor,
    showDimensions,
    board: { objects, arrows, shapes, texts, lines },
  });

  const hydrateFromEntry = (entry) => {
    setActivity(entry.activity);
    setActivityId(entry.activityId);
    setActivityRevision(entry.activityRevision ?? null);
    setPitchMode(entry.pitchMode);
    setFieldColor(entry.fieldColor);
    setShowDimensions(entry.showDimensions);
    reset(entry.board || EMPTY_BOARD);
  };

  const handleSelectTab = (tabId) => {
    if (tabId === activeTabId) return;
    const snapshot = captureLiveSnapshot();
    setWorkspaceTabs((prev) => {
      const next = prev.map((t) => (t.tabId === activeTabId ? { ...t, ...snapshot } : t));
      const target = next.find((t) => t.tabId === tabId);
      if (target) hydrateFromEntry(target);
      return next;
    });
    clearSelections();
    setActiveTool(null);
    setActiveTabId(tabId);
  };

  const handleCreateTab = () => {
    const snapshot = captureLiveSnapshot();
    const newTabId = makeTabId();
    const newEntry = {
      tabId: newTabId,
      activity: DEFAULT_ACTIVITY,
      activityId: null,
      pitchMode: "vertical-full",
      fieldColor: DEFAULT_FIELD_COLOR,
      showDimensions: true,
      board: EMPTY_BOARD,
    };
    setWorkspaceTabs((prev) => [
      ...prev.map((t) => (t.tabId === activeTabId ? { ...t, ...snapshot } : t)),
      newEntry,
    ]);
    hydrateFromEntry(newEntry);
    clearSelections();
    setActiveTool(null);
    setActiveTabId(newTabId);
  };

  // Fresh id for a duplicated canvas entity. Uses crypto.randomUUID when
  // available and falls back to a timestamp/rand suffix.
  const newEntityId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Deep-clone the currently active tab's state, assign new IDs to every
  // canvas entity so the two copies cannot share references, then insert
  // right after the current tab and switch to it.
  const handleDuplicateActivity = () => {
    // Snapshot the live state first so we clone the *current* view of the
    // active activity, even if the coach hasn't touched it since load.
    const live = captureLiveSnapshot();
    const cloneObj = (o) => ({ ...o, id: newEntityId() });
    const clonedBoard = {
      objects: (live.board.objects || []).map(cloneObj),
      arrows: (live.board.arrows || []).map(cloneObj),
      shapes: (live.board.shapes || []).map(cloneObj),
      texts: (live.board.texts || []).map(cloneObj),
      lines: (live.board.lines || []).map(cloneObj),
    };
    const clonedActivity = {
      ...live.activity,
      coachingPoints: (live.activity.coachingPoints || []).map((p) => ({
        ...p,
        id: newEntityId(),
      })),
      rotations: (live.activity.rotations || []).map((r) => ({
        ...r,
        id: newEntityId(),
      })),
    };
    // Name the copy — use "<name> Copy" when a name exists, otherwise
    // fall back to the tab-bar label (Activity N) so a blank clone still
    // reads sensibly in the tab strip.
    const idx = workspaceTabs.findIndex((t) => t.tabId === activeTabId);
    const fallback = `Activity ${idx + 1}`;
    const baseName = (live.activity.name && live.activity.name.trim()) || fallback;
    clonedActivity.name = `${baseName} Copy`;

    const newTabId = makeTabId();
    const newEntry = {
      tabId: newTabId,
      activity: clonedActivity,
      activityId: null, // Duplicates are unsaved by design.
      pitchMode: live.pitchMode,
      fieldColor: live.fieldColor,
      showDimensions: live.showDimensions,
      board: clonedBoard,
    };
    setWorkspaceTabs((prev) => {
      const withSnapshot = prev.map((t) =>
        t.tabId === activeTabId ? { ...t, ...live } : t
      );
      const i = withSnapshot.findIndex((t) => t.tabId === activeTabId);
      const next = [...withSnapshot];
      next.splice(i + 1, 0, newEntry);
      return next;
    });
    hydrateFromEntry(newEntry);
    clearSelections();
    setActiveTool(null);
    setActiveTabId(newTabId);
    toast.success("Activity duplicated");
  };

  // An entry has "meaningful content" (=> ask for confirmation before
  // delete) if it has any canvas items, a non-empty name, any duration
  // input, rules or coaching points. Fresh/empty tabs delete silently.
  const isMeaningfulEntry = (entry) => {
    if (!entry) return false;
    const b = entry.board || {};
    if ((b.objects && b.objects.length) ||
        (b.arrows && b.arrows.length) ||
        (b.shapes && b.shapes.length) ||
        (b.texts && b.texts.length) ||
        (b.lines && b.lines.length)) return true;
    const a = entry.activity || {};
    if ((a.name || "").trim()) return true;
    if ((a.rules || "").trim()) return true;
    if (Array.isArray(a.coachingPoints) && a.coachingPoints.length) return true;
    if ((a.time || "") !== "" || (a.sets || "") !== "" || (a.rest || "") !== "") return true;
    return false;
  };

  const handleDeleteTab = (tabId) => {
    if (workspaceTabs.length <= 1) return; // Never leave the workspace empty.
    // Snapshot the live state so the "current" entry reflects unsaved
    // edits when we run the meaningful-content check.
    const live = captureLiveSnapshot();
    const entries = workspaceTabs.map((t) =>
      t.tabId === activeTabId ? { ...t, ...live } : t
    );
    const idx = entries.findIndex((t) => t.tabId === tabId);
    if (idx === -1) return;
    const target = entries[idx];
    if (isMeaningfulEntry(target)) {
      const label = (target.activity?.name || "").trim() || `Activity ${idx + 1}`;
      const ok = window.confirm(
        `Delete "${label}"? This activity and everything on its pitch will be removed. This can't be undone.`
      );
      if (!ok) return;
    }
    const wasActive = tabId === activeTabId;
    const nextEntries = entries.filter((t) => t.tabId !== tabId);
    setWorkspaceTabs(nextEntries);
    if (wasActive) {
      // Prefer the tab that shifted into the deleted slot (i.e. the one
      // to the right of the deleted one), otherwise fall back to the
      // previous tab.
      const nextIdx = Math.min(idx, nextEntries.length - 1);
      const nextTab = nextEntries[nextIdx];
      hydrateFromEntry(nextTab);
      clearSelections();
      setActiveTool(null);
      setActiveTabId(nextTab.tabId);
    }
    toast.success("Activity deleted");
  };

  // Returns false when the coach cancels, so SessionsDialog can skip its
  // "Loaded …" toast.
  const handleLoadActivity = (a) => {
    if (
      shouldConfirmReplaceActiveTab({
        activeEntry: captureLiveSnapshot(),
        hasUnsavedWork: protection.hasUnsavedWork(),
      }) &&
      !window.confirm("Replace the current activity? Its unsaved changes will be lost.")
    ) {
      return false;
    }
    // Shared normaliser (lib/activityLoad): legacy half-pitch migration,
    // pitch-mode aliases, player sizes, legacy description/duration and
    // showDimensions (default true when the record has none).
    const n = normalizeLoadedActivity(a);
    reset(n.board);
    setActivity(n.activity);
    setActivityId(a.id);
    setActivityRevision(revisionOf(a));
    setPitchMode(n.pitchMode);
    setFieldColor(n.fieldColor);
    setShowDimensions(n.showDimensions);
    clearSelections();
    // A lone activity that now matches the Library copy has nothing unsaved.
    if (isSingleActivityWorkspace({ tabs: workspaceTabs, sessionId, sessionName, sessionMeta })) {
      protection.markClean();
    }
    return true;
  };

  const handleDuplicated = (a) => {
    // The duplicate action returns the newly created copy — load it as the
    // current session so the coach can immediately tweak the variant.
    handleLoadActivity(a);
  };

  // ---------------------------------------------------------------
  // Session (multi-activity) persistence
  // ---------------------------------------------------------------

  // Turn a workspace entry (or a live snapshot for the active tab) into
  // the shape the backend expects — flat activity fields + board arrays.
  const entryToActivityPayload = (entry) => ({
    id: entry.activityId || null,
    name: entry.activity.name || "",
    duration: String(computeTotalMinutes(entry.activity) || ""),
    time: entry.activity.time || "",
    sets: entry.activity.sets || "",
    rest: entry.activity.rest || "",
    rules: entry.activity.rules || "",
    coachingPoints: entry.activity.coachingPoints || [],
    rotations: entry.activity.rotations || [],
    pitchMode: entry.pitchMode,
    fieldColor: entry.fieldColor,
    showDimensions: !!entry.showDimensions,
    objects: entry.board?.objects || [],
    arrows: entry.board?.arrows || [],
    shapes: entry.board?.shapes || [],
    texts: entry.board?.texts || [],
    lines: entry.board?.lines || [],
    canonicalVersion: 2,
  });

  // Merge the live active-tab state into workspaceTabs, then map every
  // entry into a Session activity snapshot.
  const buildSessionSnapshot = () => {
    const live = captureLiveSnapshot();
    const entries = workspaceTabs.map((t) =>
      t.tabId === activeTabId ? { ...t, ...live } : t
    );
    return {
      id: sessionId,
      name: sessionName,
      date: sessionMeta.date || "",
      startTime: sessionMeta.startTime || "",
      location: sessionMeta.location || "",
      objective: sessionMeta.objective || "",
      load: sessionMeta.load || "",
      offensivePrinciples: sessionMeta.offensivePrinciples || [],
      defensivePrinciples: sessionMeta.defensivePrinciples || [],
      activities: entries.map(entryToActivityPayload),
    };
  };

  // Hydrate the workspace from a saved Session. Each embedded activity
  // becomes an independent tab with its own board and a fresh tabId so
  // reloading a Session never collides with anything currently open.
  // Returns false when the coach cancels, so SessionsDialog can skip its
  // "Loaded …" toast.
  const handleLoadSession = (s) => {
    if (!s || !Array.isArray(s.activities) || s.activities.length === 0) {
      toast.error("Session has no activities");
      return;
    }
    if (!protection.confirmDiscard("Load this session? Unsaved changes in the current workspace will be lost.")) {
      return false;
    }
    // Every embedded activity goes through the shared normaliser (same
    // legacy migration as a Library load). Session-embedded activities
    // never carry a Library id — mutating them must not touch any
    // Library entry.
    const activityToEntry = (a) => ({
      tabId: makeTabId(),
      activityId: null,
      ...normalizeLoadedActivity(a),
    });
    const nextEntries = s.activities.map(activityToEntry);
    setWorkspaceTabs(nextEntries);
    hydrateFromEntry(nextEntries[0]);
    setActiveTabId(nextEntries[0].tabId);
    setSessionId(s.id);
    setSessionRevision(revisionOf(s));
    setSessionName(s.name || "");
    setSessionMeta({
      date: s.date || "",
      startTime: s.startTime || "",
      location: s.location || "",
      objective: s.objective || "",
      load: s.load || "",
      offensivePrinciples: Array.isArray(s.offensivePrinciples) ? s.offensivePrinciples : [],
      defensivePrinciples: Array.isArray(s.defensivePrinciples) ? s.defensivePrinciples : [],
    });
    clearSelections();
    setActiveTool(null);
    protection.markClean();
    return true;
  };

  // Called by SessionsDialog after a successful save. Adopt the returned
  // ids/names so subsequent "Save" is idempotent (updates, not duplicates).
  const handleDialogClose = (open, result) => {
    if (open) return;
    setSessionsMode(null);
    if (!result) return;
    if (result.kind === "activity") {
      setActivityId(result.record.id);
      setActivityRevision(revisionOf(result.record)); // NP-4: the server's value
      setActivity((cur) => ({ ...cur, name: result.record.name || cur.name }));
      // Saving one activity only makes the workspace "saved" when it is
      // the whole workspace (no other tabs / session details).
      if (isSingleActivityWorkspace({ tabs: workspaceTabs, sessionId, sessionName, sessionMeta })) {
        protection.markClean();
      }
    } else if (result.kind === "session") {
      setSessionId(result.record.id);
      setSessionRevision(revisionOf(result.record)); // NP-4: the server's value
      setSessionName(result.record.name || "");
      protection.markClean();
    }
  };

  // A Library record was deleted. The workspace keeps its content, but it
  // must stop pointing at the deleted record: the next save then creates
  // a new record, and unsaved-work protection (DI-1) treats the content
  // as unsaved, because it no longer exists anywhere else.
  const handleLibraryRecordDeleted = (kind, id) => {
    if (!id) return;
    if (kind === "session") {
      if (id === sessionId) {
        setSessionId(null);
        setSessionRevision(null);
      }
      return;
    }
    if (id === activityId) {
      setActivityId(null);
      setActivityRevision(null);
    }
    setWorkspaceTabs((prev) =>
      prev.some((t) => t.activityId === id)
        ? prev.map((t) => (t.activityId === id ? { ...t, activityId: null, activityRevision: null } : t))
        : prev
    );
  };

  // Insert a saved Library Activity into the currently-open Session as a
  // brand-new tab. The inserted copy gets a fresh tabId, activityId=null
  // (so it never writes back into the Library), and every canvas entity
  // + coaching point receives a fresh id so it can be modified freely.
  const handleAddActivityToSession = (a) => {
    if (!a) return;
    const newTabId = makeTabId();
    const newEntry = {
      tabId: newTabId,
      activityId: null,
      ...withFreshIds(normalizeLoadedActivity(a), newEntityId),
    };
    // Snapshot the current live tab so it doesn't lose its state, then
    // append the new one and switch to it.
    const live = captureLiveSnapshot();
    setWorkspaceTabs((prev) => [
      ...prev.map((t) => (t.tabId === activeTabId ? { ...t, ...live } : t)),
      newEntry,
    ]);
    hydrateFromEntry(newEntry);
    clearSelections();
    setActiveTool(null);
    setActiveTabId(newTabId);
  };

  // ------- Session Builder view helpers -------
  const openActivityFromBuilder = (tabId) => {
    handleSelectTab(tabId);
    setView("editor");
  };
  const newBlankActivityFromBuilder = () => {
    handleCreateTab();
  };
  const addFromLibraryFromBuilder = () => {
    requireAuth("load");
  };

  // Reorder a workspace tab. Called by the Session Builder's drag-and-drop.
  // `position` is 'above' or 'below' relative to targetTabId. The currently
  // active tab is preserved — only the array order changes, no re-hydrate.
  const handleReorderTabs = (fromTabId, targetTabId, position) => {
    if (!fromTabId || !targetTabId || fromTabId === targetTabId) return;
    const live = captureLiveSnapshot();
    setWorkspaceTabs((prev) => {
      const merged = prev.map((t) =>
        t.tabId === activeTabId ? { ...t, ...live } : t
      );
      const fromIdx = merged.findIndex((t) => t.tabId === fromTabId);
      const targetIdx = merged.findIndex((t) => t.tabId === targetTabId);
      if (fromIdx === -1 || targetIdx === -1) return prev;
      const [moved] = merged.splice(fromIdx, 1);
      // Recompute target index after removal.
      const newTargetIdx = merged.findIndex((t) => t.tabId === targetTabId);
      const insertAt = position === "below" ? newTargetIdx + 1 : newTargetIdx;
      merged.splice(insertAt, 0, moved);
      return merged;
    });
  };


  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (meta && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      } else if (meta && e.key.toLowerCase() === "d") {
        // Cmd/Ctrl+D → duplicate current selection. Ignore when typing in
        // an input/textarea so the shortcut never steals text edits.
        const tag = (e.target && e.target.tagName) || "";
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        const hasSel =
          selectedId ||
          selectedShapeId ||
          selectedArrowId ||
          selectedTextId ||
          selectedLineId ||
          (multiSelection && multiSelection.length > 0);
        if (!hasSel) return;
        e.preventDefault();
        handleDuplicateSelection();
      } else if (
        (e.key === "Delete" || e.key === "Backspace") &&
        (selectedId || selectedShapeId || selectedArrowId || selectedTextId || selectedLineId || (multiSelection && multiSelection.length > 0))
      ) {
        const tag = (e.target && e.target.tagName) || "";
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          e.preventDefault();
          handleDeleteSelected();
        }
      } else if (e.key === "Escape") {
        // Cancel any active placement / draw tool and return to Select so
        // stray pitch clicks stop creating objects.
        const tag = (e.target && e.target.tagName) || "";
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          setActiveTool("select");
        }
      } else if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        const tag = (e.target && e.target.tagName) || "";
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        const hasSel =
          selectedId ||
          selectedShapeId ||
          selectedArrowId ||
          selectedTextId ||
          selectedLineId ||
          (multiSelection && multiSelection.length > 0);
        if (!hasSel) return;
        const step = e.shiftKey ? 20 : 5;
        let dx = 0;
        let dy = 0;
        if (e.key === "ArrowUp") dy = -step;
        if (e.key === "ArrowDown") dy = step;
        if (e.key === "ArrowLeft") dx = -step;
        if (e.key === "ArrowRight") dx = step;
        e.preventDefault();
        nudgeSelection(dx, dy);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo, selectedId, selectedShapeId, selectedArrowId, selectedTextId, selectedLineId, multiSelection]);

  const currentSnapshot = {
    id: activityId,
    name: activity.name,
    // Legacy `duration` field kept in sync so older exports keep working.
    duration: String(computeTotalMinutes(activity) || ""),
    time: activity.time,
    sets: activity.sets,
    rest: activity.rest,
    rules: activity.rules,
    coachingPoints: activity.coachingPoints || [],
    rotations: activity.rotations || [],
    pitchMode,
    fieldColor,
    showDimensions,
    objects,
    arrows,
    shapes,
    texts,
    lines,
  };

  // ---------------------------------------------------------------
  // DI-1 — unsaved-work protection (dirty tracking, local draft,
  // restore prompt, leave-page guard). Reads the workspace through the
  // same snapshot helpers that Save Session uses.
  // ---------------------------------------------------------------
  const applyRestoredWorkspace = (ws) => {
    setWorkspaceTabs(ws.tabs);
    setActiveTabId(ws.activeTabId);
    hydrateFromEntry(ws.tabs.find((t) => t.tabId === ws.activeTabId) || ws.tabs[0]);
    setSessionId(ws.sessionId);
    setSessionRevision(ws.sessionRevision ?? null); // NP-4 Step 6: the draft's base revision
    setSessionName(ws.sessionName);
    setSessionMeta(ws.sessionMeta);
    clearSelections();
    setActiveTool(null);
    // Resume a save the coach had asked for (e.g. before Google sign-in);
    // the existing auth effect opens it once they are signed in.
    if (ws.pendingSaveMode) setPendingSessionsMode(ws.pendingSaveMode);
    toast.success("Unsaved work restored");
  };
  const protection = useUnsavedWorkProtection({
    getSnapshot: () => buildSessionSnapshot(),
    getWorkspace: () => {
      const live = captureLiveSnapshot();
      return {
        tabs: workspaceTabs.map((t) => (t.tabId === activeTabId ? { ...t, ...live } : t)),
        activeTabId,
        sessionId,
        sessionRevision, // NP-4 Step 6: stored in the draft (tabs carry activityRevision)
        sessionName,
        sessionMeta,
        pendingSaveMode: pendingSessionsMode,
      };
    },
    makeTabId,
    onRestore: applyRestoredWorkspace,
  });

  return (
    <div
      className={
        "h-screen overflow-hidden bg-slate-50 grid " +
        (view === "builder" ? "grid-cols-1" : "grid-cols-[80px_1fr_320px]")
      }
      style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
      data-testid="training-ground-root"
    >
      {view === "editor" && (
        <Toolbar
          activeTool={activeTool}
          onSelectTool={handleSelectTool}
          keepToolActive={keepToolActive}
          onKeepToolActiveChange={setKeepToolActive}
          onDeleteSelected={handleDeleteSelected}
          onClearPitch={handleClearPitch}
          hasSelection={Boolean(
            selectedId ||
              selectedShapeId ||
              selectedArrowId ||
              selectedTextId ||
              selectedLineId ||
              (multiSelection && multiSelection.length > 0)
          )}
        />
      )}

      <main className="relative flex flex-col overflow-hidden">
        <header
          className="flex items-center px-8 py-4 border-b border-slate-200 bg-white gap-6"
          data-testid="app-header"
        >
          {/* ---- LEFT: brand + context + status ---- */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <a
              href="/"
              aria-label="The Training Ground home"
              className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded-sm"
            >
              {/* Wordmark only at ≥xl (≥1280px) — the ~320px right InfoPanel
                  and ~64px left Toolbar eat most of the viewport, so the
                  wordmark only comfortably fits above 1280px. */}
              <Logo variant="wordmark" height={22} className="hidden xl:block" data-testid="brand-logo-wordmark" />
              <Logo variant="mark" height={28} className="xl:hidden" data-testid="brand-logo-mark" />
            </a>
            <div className="hidden 2xl:block w-px h-5 bg-slate-200 shrink-0" />
            <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-400 shrink-0 hidden 2xl:inline">
              {view === "builder" ? "Session Builder" : "Activity Editor"}
            </span>
            {view === "editor" && (
              <span
                className="hidden 2xl:inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 shrink-0"
                data-testid="header-status"
                aria-label="Editor ready"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Ready
              </span>
            )}
            {activity.name && view === "editor" && (
              <span
                className="text-sm text-slate-500 truncate hidden 2xl:inline min-w-0"
                data-testid="header-activity-name"
                title={activity.name}
              >
                · {activity.name}
              </span>
            )}
          </div>

          {/* ---- CENTER: workspace mode toggle ---- */}
          <div
            className="hidden sm:flex items-center gap-0.5 p-0.5 rounded-md bg-slate-100 shrink-0"
            data-testid="view-toggle"
          >
            <button
              type="button"
              onClick={() => setView("editor")}
              data-testid="view-toggle-editor"
              aria-pressed={view === "editor"}
              className={
                "px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded transition-colors " +
                (view === "editor"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-900")
              }
            >
              Editor
            </button>
            <button
              type="button"
              onClick={() => setView("builder")}
              data-testid="view-toggle-builder"
              aria-pressed={view === "builder"}
              className={
                "px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded transition-colors " +
                (view === "builder"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-900")
              }
            >
              Session
            </button>
          </div>

          {/* ---- RIGHT: actions ---- */}
          <div className="flex items-center gap-1.5 shrink-0 flex-1 justify-end">
            <IconButton label="Undo (⌘Z)" onClick={undo} disabled={!canUndo} testId="undo-btn" className="hidden xl:inline-flex">
              <Undo2 className="w-4 h-4" />
            </IconButton>
            <IconButton label="Redo (⌘⇧Z)" onClick={redo} disabled={!canRedo} testId="redo-btn" className="hidden xl:inline-flex">
              <Redo2 className="w-4 h-4" />
            </IconButton>

            <div className="hidden xl:block w-px h-6 bg-slate-200 mx-1.5" />

            <IconButton label="New session" onClick={handleNewSession} testId="new-session-btn" className="hidden md:inline-flex">
              <FilePlus className="w-4 h-4" />
            </IconButton>
            <IconButton label="Duplicate activity" onClick={handleDuplicateActivity} testId="duplicate-activity-btn" className="hidden md:inline-flex">
              <Copy className="w-4 h-4" />
            </IconButton>
            <IconButton label="Load session" onClick={() => requireAuth("load")} testId="open-load-btn" className="hidden sm:inline-flex">
              <FolderOpen className="w-4 h-4" />
            </IconButton>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="open-save-menu-btn"
                  className="h-9 pl-2 pr-1.5 flex items-center gap-1 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
                  aria-label="Save"
                >
                  <Save className="w-4 h-4" />
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Activity</DropdownMenuLabel>
                <DropdownMenuItem
                  data-testid="menu-save-activity"
                  onSelect={() => requireAuth("save-activity")}
                >
                  Save Activity
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-save-activity-new"
                  onSelect={() => requireAuth("save-activity-new")}
                >
                  Save Activity As New
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Session</DropdownMenuLabel>
                <DropdownMenuItem
                  data-testid="menu-save-session"
                  onSelect={() => requireAuth("save-session")}
                >
                  Save Session
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-save-session-new"
                  onSelect={() => requireAuth("save-session-new")}
                >
                  Save Session As New
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="hidden xl:block w-px h-6 bg-slate-200 mx-1.5" />

            <IconButton label="Export PNG" onClick={handleExportPng} testId="export-png-btn" className="hidden xl:inline-flex">
              <Download className="w-4 h-4" />
            </IconButton>

            <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1.5" />

            <UserMenu />
          </div>
        </header>

        {view === "editor" && (
        <ActivityTabs
          tabs={workspaceTabs.map((t) =>
            t.tabId === activeTabId
              ? { tabId: t.tabId, name: activity.name }
              : { tabId: t.tabId, name: t.activity.name }
          )}
          activeTabId={activeTabId}
          onSelectTab={handleSelectTab}
          onCreateTab={handleCreateTab}
          onDeleteTab={handleDeleteTab}
        />
        )}

        {view === "builder" ? (
          <SessionBuilder
            workspaceTabs={workspaceTabs}
            activeTabId={activeTabId}
            liveActivity={activity}
            sessionName={sessionName}
            onSessionNameChange={setSessionName}
            sessionMeta={sessionMeta}
            onSessionMetaChange={setSessionMeta}
            onOpenActivity={openActivityFromBuilder}
            onNewBlankActivity={newBlankActivityFromBuilder}
            onAddFromLibrary={addFromLibraryFromBuilder}
            onReorder={handleReorderTabs}
            onGeneratePlan={() => setPlanOpen(true)}
            computeTotalMinutes={computeTotalMinutes}
          />
        ) : (
        <div className="flex-1 min-h-0 p-6 bg-slate-50">
          <div
            className="w-full h-full rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-center"
            data-testid="pitch-frame"
          >
            <Pitch
              ref={pitchRef}
              mode={pitchMode}
              fieldColor={fieldColor}
              markingsColor={getFieldPreset(fieldColor).markings}
              objects={objects}
              arrows={arrows}
              shapes={shapes}
              texts={texts}
              lines={lines}
              activeTool={activeTool}
              selectedId={selectedId}
              selectedShapeId={selectedShapeId}
              selectedArrowId={selectedArrowId}
              selectedTextId={selectedTextId}
              selectedLineId={selectedLineId}
              multiSelection={multiSelection}
              drawStyle={drawStyle}
              onSelect={selectObject}
              onSelectShape={selectShape}
              onSelectArrow={selectArrow}
              onSelectText={selectText}
              onSelectLine={selectLine}
              onToggleSelection={handleToggleSelection}
              onMoveObject={handleMoveObject}
              onMoveShape={handleMoveShape}
              onMoveText={handleMoveText}
              onMoveArrow={handleMoveArrow}
              onMoveArrowEndpoint={handleMoveArrowEndpoint}
              onCurveArrow={handleCurveArrow}
              onMoveLine={handleMoveLine}
              onMoveLineEndpoint={handleMoveLineEndpoint}
              onResizeShape={handleResizeShape}
              onMoveShapeDim={handleMoveShapeDim}
              onDragStart={handleDragStart}
              onDuplicateObject={handleDuplicateObject}
              onDuplicateShape={handleDuplicateShape}
              onDuplicateText={handleDuplicateText}
              onDuplicateArrow={handleDuplicateArrow}
              onDuplicateLine={handleDuplicateLine}
              onAddArrow={handleAddArrow}
              onAddShape={handleAddShape}
              onAddLine={handleAddLine}
              onPitchClick={handlePitchClick}
              onMarqueeSelect={handleMarqueeSelect}
              onMultiDelta={handleMultiDelta}
              onMultiScale={handleMultiScale}
              onResizeObject={handleUpdateSelectedSize}
              onRotateObject={handleUpdateSelectedRotation}
              showDimensions={showDimensions}
            />
          </div>
        </div>
        )}
      </main>

      {view === "editor" && (
      <InfoPanel
        activity={activity}
        onActivityChange={setActivity}
        activeTool={activeTool}
        pitchMode={pitchMode}
        onPitchModeChange={setPitchMode}
        showDimensions={showDimensions}
        onShowDimensionsChange={setShowDimensions}
        fieldColor={fieldColor}
        onFieldColorChange={setFieldColor}
        selectedObject={selectedObject}
        selectedShape={selectedShape}
        selectedArrow={selectedArrow}
        selectedText={selectedText}
        selectedLine={selectedLine}
        multiSelectionCount={multiSelection.length}
        drawStyle={drawStyle}
        onUpdateDrawStyle={handleUpdateDrawStyle}
        onUpdateSelectedLabel={handleUpdateSelectedLabel}
        onUpdateSelectedText={handleUpdateSelectedText}
        onUpdatePlayerDisplayMode={handleUpdatePlayerDisplayMode}
        onUpdatePlayerDisplayValue={handleUpdatePlayerDisplayValue}
        onUpdatePlayerColor={handleUpdatePlayerColor}
        onUpdatePlayerSize={handleUpdatePlayerSize}
        playerColorContext={playerColorContext}
        playerDisplayModeContext={playerDisplayModeContext}
        playerSizeContext={playerSizeContext}
        onUpdateSelectedSize={handleUpdateSelectedSize}
        onDeleteSelected={handleDeleteSelected}
        onBringForward={handleBringForward}
        onSendBackward={handleSendBackward}
        stats={stats}
      />
      )}

      <SessionsDialog
        open={sessionsMode !== null}
        onOpenChange={(v, result) => {
          if (!v) handleDialogClose(false, result);
        }}
        mode={sessionsMode || "save-activity"}
        activitySnapshot={currentSnapshot}
        sessionSnapshot={buildSessionSnapshot()}
        activityRevision={activityRevision}
        sessionRevision={sessionRevision}
        isSessionOpen={sessionId !== null}
        onLoadActivity={handleLoadActivity}
        onLoadSession={handleLoadSession}
        onAddActivityToSession={handleAddActivityToSession}
        onRecordDeleted={handleLibraryRecordDeleted}
        onSignInExpired={() => {
          // Sign-in expired mid-action (NP-2): close the dialog, keep the
          // action pending and ask the coach to sign in again. The effect
          // above resumes it once they are signed in; the workspace and
          // its DI-1 draft (which records the pending action) are untouched.
          if (sessionsMode) setPendingSessionsMode(sessionsMode);
          setSessionsMode(null);
          setAuthOpen(true);
        }}
      />

      <SessionPlanPreview
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        sessionSnapshot={buildSessionSnapshot()}
      />

      <AuthDialog
        open={authOpen}
        onBeforeRedirect={protection.prepareForRedirect}
        onOpenChange={(v, signedInUser) => {
          setAuthOpen(v);
          // Only wipe the deferred action if the user cancelled (no user
          // returned from AuthDialog). On successful sign-in the effect
          // above resumes the save/load automatically.
          if (!v && !signedInUser) setPendingSessionsMode(null);
        }}
      />

      <DraftRestoreDialog
        draft={protection.draftOffer}
        onRestore={protection.acceptDraft}
        onDiscard={protection.discardDraft}
      />

      <Toaster position="bottom-center" richColors />
    </div>
  );
}
